import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, type QualityCaseStatus, type QualityReason } from '../api';
import { useOpenDetail } from '../components/detail-link';
import { REASON_LABELS } from '../components/feedback';
import { Chip, EmptyState, ErrorState, Loading, Notice, PageIntro, useAsync } from '../components/ui';
import { formatDateTime } from '../format';

const STATUS_LABELS: Record<QualityCaseStatus, string> = {
  open: 'offen',
  confirmed: 'bestätigt',
  rejected: 'abgelehnt',
  fixed: 'behoben',
  withdrawn: 'zurückgezogen',
};

const STATUSES = Object.keys(STATUS_LABELS) as QualityCaseStatus[];
const REASONS = Object.keys(REASON_LABELS) as QualityReason[];

/**
 * Quality review (ADR-028): every "Stimmt nicht" is a case. Filter, open the
 * exact reported version with its sources and model run, record a decision,
 * and turn a confirmed defect into an evaluation fixture.
 */
export function QualityListPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? 'open';
  const reason = params.get('grund') ?? '';
  const { data, error, loading, reload } = useAsync(() => api.qualityCases(status === 'alle' ? '' : status, reason), [status, reason]);
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  const cases = data?.cases ?? [];
  return (
    <>
      <PageIntro eyebrow="Qualität" title="Gemeldete Verarbeitungsfehler.">
        Jedes „Stimmt nicht“ ist ein Fall. Die Rückmeldung verändert das Ranking nicht; bestätigte Fehler werden zu Testfällen.
      </PageIntro>
      <div className="filter-row" role="group" aria-label="Status">
        {[...STATUSES, 'alle' as const].map((s) => (
          <button key={s} type="button" className="small-button" aria-pressed={status === s} onClick={() => set('status', s)}>
            {s === 'alle' ? 'alle' : STATUS_LABELS[s]}
            {s !== 'alle' && data?.counts[s] ? ` (${data.counts[s]})` : ''}
          </button>
        ))}
      </div>
      <div className="filter-row" role="group" aria-label="Grund">
        <button type="button" className="small-button" aria-pressed={reason === ''} onClick={() => set('grund', '')}>
          jeder Grund
        </button>
        {REASONS.map((r) => (
          <button key={r} type="button" className="small-button" aria-pressed={reason === r} onClick={() => set('grund', r)}>
            {REASON_LABELS[r]}
          </button>
        ))}
      </div>
      {loading && !data && <Loading />}
      {error && <ErrorState error={error} onRetry={reload} />}
      {data && cases.length === 0 && (
        <EmptyState title="Keine Fälle in dieser Auswahl.">
          <p className="muted">Fälle entstehen, wenn du in den Details einer Meldung „Stimmt nicht“ speicherst.</p>
        </EmptyState>
      )}
      <ul className="case-list">
        {cases.map((c) => (
          <li key={c.id}>
            <div className="spread">
              <Link to={`/qualitaet/${c.id}`}>
                <strong>{c.title || c.card_version_id}</strong>
              </Link>
              <Chip kind={c.status === 'open' ? 'warn' : c.status === 'confirmed' ? 'accent' : 'neutral'}>{STATUS_LABELS[c.status]}</Chip>
            </div>
            <div className="meta">
              <span>{c.reasons.length ? c.reasons.map((r) => REASON_LABELS[r]).join(', ') : 'ohne Grund'}</span>
              <span>Stufe: {c.stages.join(', ') || 'unbestimmt'}</span>
              <span>gemeldet {formatDateTime(c.created_at)}</span>
              {c.fixture_ref && <span>Testfall: {c.fixture_ref}</span>}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

export function QualityCasePage() {
  const { id = '' } = useParams();
  const { data, error, loading, reload } = useAsync(() => api.qualityCase(id), [id]);
  const open = useOpenDetail();
  const [status, setStatus] = useState<QualityCaseStatus | null>(null);
  const [resolution, setResolution] = useState<string | null>(null);
  const [required, setRequired] = useState('');
  const [forbidden, setForbidden] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [fixture, setFixture] = useState<{ path: string; json: string } | null>(null);

  if (loading && !data) return <Loading label="Fall wird geladen…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return null;
  const c = data.case;
  const curStatus = status ?? (c.status === 'withdrawn' ? 'open' : c.status);
  const curResolution = resolution ?? c.resolution ?? '';

  const review = async () => {
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      await api.reviewQualityCase(c.id, { status: curStatus as QualityCaseStatus, resolution: curResolution.trim() || null });
      setMsg('Entscheid gespeichert.');
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const exportFixture = async () => {
    setBusy(true);
    setErr('');
    try {
      const terms = (t: string) => t.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
      const r = await api.qualityFixture(c.id, { required_terms: terms(required), forbidden_terms: terms(forbidden) });
      setFixture({ path: r.path, json: JSON.stringify(r.fixture, null, 2) + '\n' });
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Export fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    if (!fixture) return;
    const url = URL.createObjectURL(new Blob([fixture.json], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = fixture.path.split('/').pop() ?? 'fixture.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const v = data.card.version;
  return (
    <>
      <header className="page-intro">
        <p className="eyebrow">
          <Link to="/qualitaet">Qualität</Link> · Fall {c.id.slice(0, 8)} · {STATUS_LABELS[c.status]}
        </p>
        <h1>{v.title}</h1>
        <p className="page-lead">
          {c.reasons.length ? c.reasons.map((r) => REASON_LABELS[r]).join(', ') : 'Ohne Grund gemeldet'} · Stufe {c.stages.join(', ') || 'unbestimmt'} ·
          gemeldet {formatDateTime(c.created_at)}
        </p>
      </header>

      <div className="subject-grid">
        <div>
          <section className="panel">
            <h2>Gemeldete Version</h2>
            <p className="small">
              Version {v.version} ({v.id}), erstellt {formatDateTime(v.created_at)}.{' '}
              <button type="button" className="text-button" onClick={(e) => open(v.id, e.currentTarget)}>
                In den Details öffnen
              </button>
              {data.edition_id && (
                <>
                  {' · '}
                  <Link to={`/ausgaben/${data.edition_id}`}>Gemeldet aus der {data.edition?.label ?? 'Ausgabe'}</Link>
                </>
              )}
            </p>
            <p className="lead">{v.summary}</p>
            {c.note && (
              <Notice kind="info" title="Deine Erklärung (privat, nicht im Testfall)">
                {c.note}
              </Notice>
            )}
          </section>
          <section>
            <h2>Quellen, wie das Modell sie las</h2>
            {data.sources.map((s) => (
              <details key={s.revision_id} className="disclosure">
                <summary>
                  {s.source_name || s.source_id}: {s.title} <span className="muted small">({s.text_source})</span>
                </summary>
                {s.url && (
                  <p className="small">
                    <a href={s.url} target="_blank" rel="noopener noreferrer">
                      Original ↗
                    </a>
                  </p>
                )}
                {s.text_available ? <div className="source-text">{s.text || '(leer)'}</div> : <p className="warn-text small">Quelltext nicht mehr vorhanden.</p>}
              </details>
            ))}
          </section>
        </div>
        <aside>
          <section className="panel">
            <h2>Verarbeitung</h2>
            <dl className="kv">
              <dt>Erzeuger</dt>
              <dd>
                {c.producer} {c.producer_version}
              </dd>
              <dt>Modell</dt>
              <dd>{data.model_run ? `${data.model_run.provider} / ${data.model_run.model} (${data.model_run.status})` : 'kein Modelllauf'}</dd>
              <dt>Prompt</dt>
              <dd>{c.prompt_version ?? '–'}</dd>
              <dt>Cluster</dt>
              <dd>{c.cluster_id ? `${c.cluster_id.slice(0, 8)} · Version ${c.cluster_version ?? '?'}` : '–'}</dd>
            </dl>
          </section>
          <section className="panel">
            <h2>Entscheid</h2>
            <label className="field-label" htmlFor="case-status">
              Status
            </label>
            <select id="case-status" value={curStatus} onChange={(e) => setStatus(e.target.value as QualityCaseStatus)}>
              {(['open', 'confirmed', 'rejected', 'fixed'] as QualityCaseStatus[]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </select>
            <label className="field-label" htmlFor="case-resolution">
              Befund (kommt in den Testfall; keine persönlichen Angaben)
            </label>
            <textarea id="case-resolution" rows={4} maxLength={2000} value={curResolution} onChange={(e) => setResolution(e.target.value)} />
            <div className="button-row">
              <button type="button" className="button primary" disabled={busy} onClick={review}>
                Entscheid speichern
              </button>
            </div>
            <p role="status" className="muted small">
              {msg}
            </p>
          </section>
          <section className="panel">
            <h2>Als Testfall übernehmen</h2>
            {c.status !== 'confirmed' && c.status !== 'fixed' ? (
              <p className="muted small">Erst ein bestätigter Fall wird zum Testfall.</p>
            ) : (
              <>
                <p className="muted small">
                  Enthält nur öffentliche Quelltexte, die gemeldete Ausgabe und deinen Befund, nie die private Erklärung. Die Datei gehört nach{' '}
                  <code>evals/regressions/</code>; <code>make eval</code> führt sie aus.
                </p>
                <label className="field-label" htmlFor="fx-req">
                  Muss enthalten (ein Begriff pro Zeile)
                </label>
                <textarea id="fx-req" rows={2} value={required} onChange={(e) => setRequired(e.target.value)} />
                <label className="field-label" htmlFor="fx-forb">
                  Darf nicht enthalten
                </label>
                <textarea id="fx-forb" rows={2} value={forbidden} onChange={(e) => setForbidden(e.target.value)} />
                <div className="button-row">
                  <button type="button" className="small-button" disabled={busy} onClick={exportFixture}>
                    Testfall erzeugen
                  </button>
                  {fixture && (
                    <button type="button" className="small-button" onClick={download}>
                      {fixture.path.split('/').pop()} herunterladen
                    </button>
                  )}
                </div>
                {c.fixture_ref && <p className="small">Zuletzt erzeugt: {c.fixture_ref}</p>}
              </>
            )}
          </section>
          {err && <Notice kind="danger">{err}</Notice>}
        </aside>
      </div>
    </>
  );
}
